import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import {
  getTournamentsV2,
  createTournamentV2,
} from '../services/tournamentsV2';
import {
  TournamentV2,
  CreateTournamentV2Params,
} from '../types';
import './TournamentManagement.css';

const TournamentManagement: React.FC = () => {
  const { t } = useTranslation();
  const { clubId } = useParams<{ clubId: string }>();
  const navigate = useNavigate();
  const [tournaments, setTournaments] = useState<TournamentV2[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);

  const clubIdNum = clubId ? parseInt(clubId, 10) : null;

  useEffect(() => {
    if (clubIdNum) {
      loadTournaments();
    }
  }, [clubIdNum]);

  const loadTournaments = async () => {
    if (!clubIdNum) return;
    
    try {
      setLoading(true);
      setError(null);
      const data = await getTournamentsV2(clubIdNum);
      setTournaments(data);
    } catch (err) {
      console.error('Error loading tournaments:', err);
      setError(err instanceof Error ? err.message : 'Failed to load tournaments');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTournament = async (tournamentData: Omit<CreateTournamentV2Params, 'club_id'>) => {
    try {
      const newTournament = await createTournamentV2({
        ...tournamentData,
        club_id: clubIdNum!,
      });
      setTournaments(prev => [newTournament, ...prev]);
      setShowCreateModal(false);
    } catch (err) {
      console.error('Error creating tournament:', err);
      alert(err instanceof Error ? err.message : 'Failed to create tournament');
    }
  };


  if (!clubIdNum) {
    return <div className="error-message">Invalid club ID</div>;
  }

  if (loading) {
    return <div className="loading">{t('common.loading')}</div>;
  }

  if (error) {
    return (
      <div className="error-container">
        <div className="error-message">{error}</div>
        <button onClick={loadTournaments} className="btn btn-primary">
          {t('common.retry')}
        </button>
      </div>
    );
  }

  return (
    <div className="tournament-management-page">
      <div className="page-header">
        <div className="header-content">
          <h1>{t('tournaments.management.title')}</h1>
          <button 
            onClick={() => navigate(-1)}
            className="btn btn-secondary"
          >
            ← {t('common.back')}
          </button>
        </div>
        <button 
          onClick={() => setShowCreateModal(true)}
          className="btn btn-primary"
        >
          {t('tournaments.actions.create')}
        </button>
      </div>

      <div className="tournaments-grid">
        {tournaments.map(tournament => (
          <TournamentCardV2
            key={tournament.id}
            tournament={tournament}
          />
        ))}
      </div>

      {/* Create Tournament Modal */}
      {showCreateModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>{t('tournaments.actions.create')}</h3>
              <button onClick={() => setShowCreateModal(false)} className="close-button">&times;</button>
            </div>
            <div className="modal-body">
              <TournamentCreateForm 
                onSubmit={handleCreateTournament}
                onCancel={() => setShowCreateModal(false)}
              />
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

// Tournament Card Component (extracted from TournamentManagementV2)
interface TournamentCardV2Props {
  tournament: TournamentV2;
}

const TournamentCardV2: React.FC<TournamentCardV2Props> = ({
  tournament,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();


  const handleCardClick = () => {
    navigate(`/clubs/${tournament.club_id}/tournaments/${tournament.id}`);
  };

  return (
    <div className="tournament-card-v2" onClick={handleCardClick}>
      <div className="tournament-card-header">
        <div className="tournament-card-title">
          <h3>{tournament.name}</h3>
        </div>
        <span className={`status-badge status-${tournament.status}`}>
          {t(`tournaments.status.${tournament.status}`)}
        </span>
      </div>

      <div className="tournament-card-body">
        <p className="tournament-description">{tournament.description}</p>
        
        <div className="tournament-details">
          <div className="detail-item">
            <span className="label">{t('tournaments.formatLabel')}:</span>
            <span className="value">{t(`tournaments.formats.${tournament.format}`)}</span>
          </div>
          <div className="detail-item">
            <span className="label">{t('tournaments.participantsLabel')}:</span>
            <span className="value">
              {tournament.settings?.participant_count || 16} {t('tournaments.maxParticipants')}
            </span>
          </div>
          <div className="detail-item">
            <span className="label">{t('tournaments.startDate')}:</span>
            <span className="value">
              {tournament.settings?.start_date 
                ? new Date(tournament.settings.start_date).toLocaleDateString()
                : t('common.tbd')
              }
            </span>
          </div>
        </div>
      </div>

    </div>
  );
};

// Tournament Create Form Component
interface TournamentCreateFormProps {
  onSubmit: (data: Omit<CreateTournamentV2Params, 'club_id'>) => void;
  onCancel: () => void;
}

const TournamentCreateForm: React.FC<TournamentCreateFormProps> = ({ onSubmit, onCancel }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState<Omit<CreateTournamentV2Params, 'club_id'>>({
    name: '',
    description: '',
    format: 'single_elimination',
    participant_count: 16,
    start_date: '',
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="tournament-create-form">
      <div className="form-group">
        <label htmlFor="name">{t('tournaments.name')} *</label>
        <input
          type="text"
          id="name"
          value={formData.name}
          onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
          required
        />
      </div>

      <div className="form-group">
        <label htmlFor="description">{t('tournaments.description')}</label>
        <textarea
          id="description"
          value={formData.description}
          onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
          rows={3}
        />
      </div>

      <div className="form-group">
        <label htmlFor="format">{t('tournaments.formatLabel')} *</label>
        <select
          id="format"
          value={formData.format}
          onChange={(e) => setFormData(prev => ({ ...prev, format: e.target.value as any }))}
          required
        >
          <option value="single_elimination">{t('tournaments.formats.single_elimination')}</option>
          <option value="double_elimination">{t('tournaments.formats.double_elimination')}</option>
        </select>
      </div>

      <div className="form-group">
        <label htmlFor="participant_count">{t('tournaments.participantsLabel')} *</label>
        <select
          id="participant_count"
          value={formData.participant_count || 16}
          onChange={(e) => setFormData(prev => ({ 
            ...prev, 
            participant_count: parseInt(e.target.value) as 8 | 16 | 32 
          }))}
          required
        >
          <option value={8}>8 {t('tournaments.maxParticipants')}</option>
          <option value={16}>16 {t('tournaments.maxParticipants')}</option>
          <option value={32}>32 {t('tournaments.maxParticipants')}</option>
        </select>
      </div>

      <div className="form-group">
        <label htmlFor="start_date">{t('tournaments.startDate')}</label>
        <input
          type="datetime-local"
          id="start_date"
          value={formData.start_date || ''}
          onChange={(e) => setFormData(prev => ({ 
            ...prev, 
            start_date: e.target.value 
          }))}
        />
      </div>

      <div className="form-actions">
        <button type="button" onClick={onCancel} className="btn btn-secondary">
          {t('common.cancel')}
        </button>
        <button type="submit" className="btn btn-primary">
          {t('tournaments.actions.create')}
        </button>
      </div>
    </form>
  );
};

export default TournamentManagement;